// Helpers for turning Quill 2 output into the HTML/text we store and email.

// Quill wraps embeds in zero-width no-break spaces ("guards") so the cursor can sit either side of them.
const GUARD = /\uFEFF/g;

export function stripGuards(text: string): string {
  return text.replace(GUARD, '');
}

export function escapeHtml(text: string): string {
  return text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}

/**
 * Quill 2.0.3's getSemanticHTML() turns every space into &nbsp;, which stops emails wrapping.
 * Turn them back into normal spaces, but keep runs of spaces visible by alternating
 * space / &nbsp; (a single &nbsp; becomes a plain space).
 */
export function normalizeNbsp(html: string): string {
  return html.replace(/(?:&nbsp;)+/g, run => {
    const count = run.length / '&nbsp;'.length;
    return ' ' + '&nbsp;'.repeat(count - 1);
  });
}

// Quill's caret placeholder (used while a format is toggled with nothing typed yet) can leak into the HTML.
const CURSOR_SPAN = /<span class="ql-cursor">[^<]*<\/span>/g;

/** HTML for the email body: semantic HTML from Quill with normal spaces, no embed guards or caret marker. */
export function toStoredHtml(semanticHtml: string): string {
  return normalizeNbsp(stripGuards(semanticHtml.replace(CURSOR_SPAN, '')).replace(/\u00a0/g, '&nbsp;'));
}

const ENTITIES: Record<string, string> = { '&nbsp;': ' ', '&lt;': '<', '&gt;': '>', '&quot;': '"', '&#39;': "'", '&amp;': '&' };

/** Decode the handful of entities the editor's HTML contains (&amp; last, so "&amp;lt;" stays "&lt;"). */
export function decodeEntities(text: string): string {
  return text.replace(/&(?:nbsp|lt|gt|quot|#39|amp);/g, entity => ENTITIES[entity]);
}

/** Plain text for the subject line: tags stripped, entities decoded, no embed guards. */
export function toStoredText(html: string): string {
  return decodeEntities(stripGuards(html.replace(/<\/?[^>]+(>|$)/g, ''))).replace(/\u00a0/g, ' ');
}

const MERGE_TAG = /\{\{\s*(\w+)\s*\}\}/g;

/** Split text into plain parts and merge tags: "Hi {{name}}!" -> ["Hi ", { tag: "{{name}}" }, "!"]. */
export function splitMergeTags(text: string): Array<string | { tag: string }> {
  const parts: Array<string | { tag: string }> = [];
  let last = 0;
  for (const match of stripGuards(text).matchAll(MERGE_TAG)) {
    if (match.index! > last) parts.push(stripGuards(text).slice(last, match.index));
    parts.push({ tag: `{{${match[1]}}}` });
    last = match.index! + match[0].length;
  }
  if (last < stripGuards(text).length) parts.push(stripGuards(text).slice(last));
  return parts;
}
